// MUI-free copy of Ui-components/questions (with its MUI-Components/MUI-Accordian SimpleAccordion inlined). Answers stay in
// the server HTML: collapsed panels are hidden, not unmounted, as with MUI's Accordion.
import React, {Fragment, memo, useCallback, useState} from 'react';
import parse from 'html-react-parser';
import Container from '@/components/atoms/Container';
import Collapse from '@/components/atoms/Collapse';
import Icon from '@/components/atoms/Icon';
import type {AccordionItem, SubpartAccordionItem} from '@/types';
import styles from './Questions.module.scss';

export interface QuestionsProps {
    accordianData?: AccordionItem[];
    /** Render structured (`{header, subpart, body, footer}`) answers; plain string answers still render as text. */
    subpart?: boolean;
}

const cx = (...names: Array<string | false | undefined>) => names.filter(Boolean).join(' ');

// SimpleAccordion always passed this to every AccordionSummary (globals: `.accordion-grid-main .Mui-expanded .panel1a-header`).
const SUMMARY_CLASS = 'panel1a-header';
const REGION_ID = 'panel1a-content';

// ─── Answer bodies ──────────────────────────────────────────────────────────────

const SubpartAnswer = ({answer}: {answer: SubpartAccordionItem['answer']}) => (
    <div className={cx('muiAccordionDetails answer-detail-wrapper', styles.details, styles.subDetails)}>
        <div className={cx('answer-detail-header data_links ', styles.text)}>{answer?.header ? parse(answer.header) : ''}</div>
        {answer?.subpart?.map((answerData, answerIndex) => (
            <div key={answerIndex} className="sub-answer-wrapper">
                <div className={cx('answer-sub-heading font-regular-text', styles.text)}>{answerData?.heading}</div>
                {answerData?.keypoints?.map((subdata, keypointsIndex) => (
                    <Fragment key={keypointsIndex}>
                        <Container>
                            <div className={cx('answer-key-head', styles.text)}>{subdata?.keyHeading}</div>
                            <ol className={cx('answer-list-wrapper', styles.list)}>
                                <li className={cx('answer-list-items', styles.listItem)}>
                                    <div className={cx('answer-key-body data_links answer', styles.text)}>
                                        {subdata?.keyBody ? parse(subdata.keyBody) : ''}
                                    </div>
                                </li>
                            </ol>
                        </Container>
                    </Fragment>
                ))}
            </div>
        ))}
        <p className={cx('answer-detail-footer data_links answer', styles.text)}>{answer?.body ? parse(answer.body) : ''}</p>
        <div className={cx('answer-detail-footer', styles.text)}>{answer?.footer ? parse(answer.footer) : ''}</div>
    </div>
);

// ─── One accordion (MUI Accordion + AccordionSummary + Collapse markup) ─────────

interface QuestionAccordionProps {
    data: AccordionItem;
    index: number;
    isExpanded: boolean;
    subpart?: boolean;
    onToggle: (index: number) => void;
}

const QuestionAccordion = ({data, index, isExpanded, subpart, onToggle}: QuestionAccordionProps) => {
    const toggle = () => onToggle(index);
    const summaryId = `panel${index}a-header`;

    // MUI ButtonBase keyboard behaviour on a non-button element: Enter fires on keydown, Space on keyup.
    const onKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.target !== e.currentTarget) return;
        if (e.key === ' ') e.preventDefault();
        if (e.key === 'Enter') {
            e.preventDefault();
            toggle();
        }
    };
    const onKeyUp = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.target === e.currentTarget && e.key === ' ') toggle();
    };

    // `Mui-expanded` is kept on the expanded elements because globals.scss keys the expanded summary gradient on it.
    const expandedClass = isExpanded && 'Mui-expanded';

    return (
        <div className={cx(styles.accordion, expandedClass, isExpanded && styles.expanded)}>
            <div
                className={cx(styles.summary, expandedClass, SUMMARY_CLASS)}
                role="button"
                tabIndex={0}
                aria-expanded={isExpanded}
                aria-controls={REGION_ID}
                id={summaryId}
                onClick={toggle}
                onKeyDown={onKeyDown}
                onKeyUp={onKeyUp}
            >
                <div className={cx(styles.summaryContent, expandedClass)}>
                    <h3 className={cx('question font-regular-text', styles.question)}>{data.question ? parse(data.question) : ''}</h3>
                </div>
                <div className={cx(styles.expandIconWrapper, expandedClass)}>
                    {isExpanded ? <Icon name="Remove" className={cx('remove-icon', styles.removeIcon)} /> : <Icon name="Add" />}
                </div>
            </div>
            <Collapse in={isExpanded}>
                <div aria-labelledby={summaryId} id={REGION_ID} role="region">
                    {subpart && typeof data.answer !== 'string' ? (
                        <SubpartAnswer answer={data.answer} />
                    ) : (
                        <div className={cx('muiAccordionDetails', styles.details)}>
                            <p className={cx('data_links answer font-regular-text', styles.text)}>
                                {data?.answer ? parse(data.answer as string) : ''}
                            </p>
                        </div>
                    )}
                </div>
            </Collapse>
        </div>
    );
};

// ─── Main component ─────────────────────────────────────────────────────────────

const Questions = ({accordianData, subpart}: QuestionsProps) => {
    const [expanded, setExpanded] = useState<number | false>(false);

    const handleToggle = useCallback((panel: number) => {
        setExpanded((current) => (current === panel ? false : panel));
    }, []);

    return (
        <div>
            <h2 className={cx('font-headers', styles.heading)}>Frequently Asked Questions</h2>
            <div>
                <div className={cx('faq-inner-wrapper', styles.wrapper)}>
                    <div className={styles.grid}>
                        {accordianData?.map((data, index) => (
                            <div key={index} className={cx('accordion-grid-main', styles.item)}>
                                <QuestionAccordion
                                    data={data}
                                    index={index}
                                    isExpanded={expanded === index}
                                    subpart={subpart}
                                    onToggle={handleToggle}
                                />
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default memo(Questions);
